import { useLocation, useNavigate } from "react-router-dom";
import Icon from "@ant-design/icons";
import { Icons } from "../assets/icons";
import SiderMenu from "../controls/SiderMenu";
import { useEffect, useState } from "react";
import { HorizontalMenuItemsKey } from "../controls/HorizontalMenu.default";
import { SiderMenuItem } from "../controls/SiderMenu.default";
import { useProcessItems } from "./KeyProcessesOverview/useProcessItems";

export interface NavigationMenuProps {
  selectedMainMenu?: string;
}

function NavigationMenu(props: NavigationMenuProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [key, setKey] = useState<string | null>();

  // Load the roadmap stages once so the Processes submenu can list each stage
  // with the right gating (completed / current / locked). Hook is at the top
  // level so React is happy; the actual fetch is cheap and only its data gets
  // read when the Processes section is visible.
  const { processItems, notStarted, noRoadmap } = useProcessItems();

  useEffect(() => {
    setKey(location.pathname?.replace(/^\/|\/$/, "")?.trim() ?? null);
  }, [location]);

  function onSelectItem(key: string): void {
    navigate(key, {
      replace: true,
    });
  }

  function getMenuItems() {
    switch (props.selectedMainMenu) {
      case HorizontalMenuItemsKey.Profile:
        return [
          {
            key: "profile",
            label: "ACCOUNT",
            icon: (
              <Icon className="-ml-0.5 text-2xl" component={Icons.Corporate} />
            ),
          },
          {
            key: "profile/users",
            label: "USERS",
            icon: (
              <Icon className="-ml-0.5 text-2xl" component={Icons.GroupIcon} />
            ),
          },
          {
            key: "profile/employees",
            label: "EMPLOYEES",
            icon: (
              <Icon
                className="-ml-0.5 text-2xl"
                component={Icons.Diversity3Icon}
              />
            ),
          },
          {
            key: "profile/analytics",
            label: "ANALYTICS",
            icon: (
              <Icon
                className="-ml-0.5 text-2xl"
                component={Icons.QueryStatsIcon}
              />
            ),
          },
          {
            key: "profile/metrics",
            label: "ORGANIZATIONAL METRICS",
            icon: (
              <Icon
                className="-ml-0.5 text-2xl"
                component={Icons.QueryStatsIcon}
              />
            ),
          },
          {
            key: "profile/calendar",
            label: "CALENDAR",
            icon: (
              <Icon
                className="-ml-0.5 text-2xl"
                component={Icons.CalendarMonthIcon}
              />
            ),
          },
        ];
      case HorizontalMenuItemsKey.Processes: {
        // Always keep Overview on top. The data-driven roadmap map is still
        // the primary way in — the per-stage items below are a convenience so
        // the operator can see WHERE they are in the journey at a glance,
        // with locked/current/completed gating that mirrors the pin map.
        const items: SiderMenuItem[] = [
          {
            key: "processes/overview",
            label: "OVERVIEW",
            icon: (
              <Icon
                className="-ml-0.5 text-2xl"
                component={Icons.Diversity2Icon}
              />
            ),
          },
        ];

        if (!noRoadmap && processItems.length) {
          const lastCompletedIndex =
            processItems.findIndex((p) => !p.isCompleted) - 1;
          processItems.forEach((p, i) => {
            // A stage is reachable when the journey has started and it's
            // either completed OR the immediate next one. Everything else is
            // locked so the sider matches the roadmap pin gating.
            const unlocked =
              !notStarted &&
              (p.isCompleted || i === lastCompletedIndex + 1);
            items.push({
              key: `processes${p.componentUrl}`,
              label: p.Title.toUpperCase(),
              icon: (
                <Icon
                  className="-ml-0.5 text-2xl"
                  component={Icons.Diversity2Icon}
                />
              ),
              disabled: !unlocked,
            });
          });
        }

        return items;
      }
      case HorizontalMenuItemsKey.Actions:
        return [
          {
            key: "actions/overview",
            label: "OVERVIEW",
            icon: (
              <Icon
                className="-ml-0.5 text-2xl"
                // Todo: Update the Icon
                component={Icons.Flag}
              />
            ),
          },
          {
            key: "actions/assign-oceaniv",
            label: "ASSIGN OCEAN IV",
            icon: (
              <Icon
                className="-ml-0.5 text-2xl"
                component={Icons.PersonAddIcon}
              />
            ),
          },
          {
            key: "actions/create-programs",
            label: "CREATE PROGRAMS",
            icon: (
              <Icon className="-ml-0.5 text-2xl" component={Icons.Search} />
            ),
          },
          {
            key: "actions/send-updates",
            label: "SEND UPDATES",
            icon: (
              <Icon
                className="-ml-0.5 text-2xl"
                component={Icons.OutgoingEmailIcon}
              />
            ),
          },
          {
            key: "actions/send-survey",
            label: "SEND SURVEY",
            icon: (
              <Icon
                className="-ml-0.5 text-2xl"
                component={Icons.ChecklistIcon}
              />
            ),
          },
          {
            key: "actions/create-events",
            label: "CREATE EVENTS",
            icon: (
              <Icon
                className="-ml-0.5 text-2xl"
                component={Icons.EventListIcon}
              />
            ),
          },
          {
            key: "actions/create-rewards",
            label: "CREATE REWARDS",
            icon: (
              <Icon className="-ml-0.5 text-2xl" component={Icons.TrophyIcon} />
            ),
          },
          {
            key: "actions/healthcare-plan",
            label: "HEALTHCARE PLAN",
            icon: (
              <Icon
                className="-ml-0.5 text-2xl"
                // Todo: Update the Icon
                component={Icons.Flag}
              />
            ),
          },
          {
            key: "actions/leadership-board",
            label: "POINTS SYSTEM LEADERSHIP BOARD",
            icon: (
              <Icon
                className="-ml-0.5 text-2xl"
                // Todo: Update the Icon
                component={Icons.Flag}
              />
            ),
          },
        ];
      default:
        return [];
    }
  }

  const items = getMenuItems();
  return items.length > 0 ? (
    <SiderMenu
      selectedKey={key}
      items={getMenuItems() as SiderMenuItem[]}
      onSelect={onSelectItem}
    />
  ) : null;
}

export default NavigationMenu;
